import type { PortalScope } from "./portal-visibility"
import { shownPortalIds } from "./portal-visibility"

export interface CardCopy {
	title: string
	description: string
}

export interface PublicCopy {
	publicTitle?: string | null
	publicDescription?: string | null
}

/**
 * The item's public copy after publishing: the card's wording, drawn from the
 * customer's own request, fills only what the team hasn't written yet.
 */
export function publicCopyFor(item: PublicCopy, card: CardCopy) {
	return {
		publicTitle: item.publicTitle?.trim() ? item.publicTitle : card.title,
		publicDescription: item.publicDescription?.trim()
			? item.publicDescription
			: card.description,
	}
}

export interface CardCandidate extends PortalScope, PublicCopy {
	id: string
}

export type CardTarget<T extends CardCandidate> =
	| { kind: "linked"; type: "issue" | "project"; item: T; shown: boolean }
	| { kind: "match"; type: "issue"; item: T }
	| { kind: "create" }

/**
 * What the card offers. An item the thread already links to wins, even when a
 * routine matched it: the first one not yet on the request's portal, else the
 * first linked one. Then the issue the draft matched, then a new one.
 */
export function resolveCardTarget<T extends CardCandidate>({
	portalId,
	linkedIssues,
	linkedProjects,
	matchedIssue,
}: {
	portalId: string
	linkedIssues: readonly T[]
	linkedProjects: readonly T[]
	matchedIssue: T | null
}): CardTarget<T> {
	const linked = [
		...linkedProjects.map((item) => ({ type: "project" as const, item })),
		...linkedIssues.map((item) => ({ type: "issue" as const, item })),
	]
	const isShown = (item: T) => shownPortalIds(item).includes(portalId)
	const missing = linked.find(({ item }) => !isShown(item))
	if (missing) return { kind: "linked", ...missing, shown: false }
	const first = linked[0]
	if (first) return { kind: "linked", ...first, shown: true }
	if (matchedIssue) return { kind: "match", type: "issue", item: matchedIssue }
	return { kind: "create" }
}
